// src/api/axiosInstance.js
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { API_BASE_URL } from "../../config/api-config";
import tokenService from "../../services/token-service";
import logger from "../../helpers/logger";
import { authRequest } from "../../authConfig";
import { logoutUser, msalInstance } from "./msal-instance";
import { toast } from "react-toastify";
import roleService from "../../services/role-service";

let globalController = new AbortController();

export const cancelAllRequests = () => {
    globalController.abort(); // cancel all ongoing requests
    globalController = new AbortController(); // create new controller
};

let refreshTokenPromise = null;
const MAX_RETRIES = 3;

const axiosIns = axios.create({
    baseURL: `${API_BASE_URL}/api/`,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request Interceptor
axiosIns.interceptors.request.use(
    (config) => {
        const token = tokenService.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.signal = globalController.signal;
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor
axiosIns.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) return Promise.reject(error);

        if (!originalRequest._retryCount) {
            originalRequest._retryCount = 0;
        }

        if (error.response?.status === 401 && originalRequest._retryCount < MAX_RETRIES) {
            originalRequest._retryCount += 1;

            // If there's already a refresh in progress, wait for it
            if (!refreshTokenPromise) {
                refreshTokenPromise = (async () => {
                    try {
                        const accounts = msalInstance.getAllAccounts();
                        if (!accounts || accounts.length === 0) {
                            logger.warn("⚠️ Axios interceptor: No accounts found for token refresh");
                            throw new Error("No accounts available");
                        }

                        const tokenResponse = await msalInstance.acquireTokenSilent({
                            ...authRequest,
                            account: accounts[0],
                        });

                        const newAccessToken = tokenResponse.accessToken;
                        tokenService.setToken(newAccessToken);
                        roleService.clearCache();
                        return newAccessToken;
                    } catch (e) {
                        // Handle InteractionRequiredAuthError - session doesn't exist in Azure AD
                        if (e instanceof InteractionRequiredAuthError) {
                            logger.warn("⚠️ Axios interceptor: Session expired (interaction required)", {
                                errorCode: e.errorCode,
                                errorMessage: e.errorMessage,
                            });
                            // Re-throw original error type so downstream handlers can use instanceof checks
                            throw e;
                        }
                        logger.error("Token refresh failed", e);
                        throw e;
                    } finally {
                        // Reset shared promise after it finishes
                        refreshTokenPromise = null;
                    }
                })();
            }

            try {
                const newAccessToken = await refreshTokenPromise;

                // Update token service with new token
                tokenService.setToken(newAccessToken);
                logger.log("✅ Token refreshed and updated in token service");

                roleService.clearCache();

                // Retry the original request with new token
                originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                return axiosIns(originalRequest);
            } catch (e) {
                logger.warn("⚠️ Token refresh attempt failed", e);
                return Promise.reject(e);
            }
        }

        if (originalRequest._retryCount >= MAX_RETRIES) {
            logger.error("Max retries reached. Logging out.");
            toast.error("Session expired. Redirecting to login...");
            logoutUser();
        }

        return Promise.reject(error);
    }
);


export default axiosIns;
